import {
  CASH_LOCATION_LABELS,
  CORRECTION_KIND_LABELS,
  PAY_IN_CATEGORIES,
  PAY_IN_LABELS,
  PAY_OUT_CATEGORIES,
  PAY_OUT_LABELS,
  SETTLED_TO,
  SETTLED_TO_LABELS,
  SETTLEMENT_MODES,
  SETTLEMENT_MODE_LABELS,
  cashLocationsFor,
  correctionNeedsRecentAuth,
  describeOverShort,
  minorToInput,
  parseMoneyToMinor,
} from '@didto/domain';
import type {
  CashLocation,
  CorrectionKind,
  LinkablePayIn,
  PayInCategory,
  PayOutCategory,
  SettledTo,
  SettlementMode,
  ShiftCorrectPayload,
  ShiftCorrectionView,
  ShiftDetailMovement,
  ShiftPaymentView,
  ShiftReversalView,
} from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Notice,
  Pills,
  Select,
  Sheet,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useOwnerReview } from '@/app/ownerReview';
import { useToast } from '@/app/toast';
import { errorText } from '@/lib/errors';
import { openingCountText } from '@/lib/openingCount';
import { useOnline } from '@/lib/network';
import { fetchPaymentMethods, paymentKeys } from '@/lib/payments';
import type { ShiftDetailView } from '@/lib/review';
import { LEDGER_ROW_TONE } from '@/lib/status';
import { supabase } from '@/lib/supabase';
import { OwnerEntrySheet, type OwnerEntryTarget } from './ownerEntries';
import { useFormats } from './reviewItems';

/**
 * Closed-shift corrections (Phase 7A-2B, ADR-027). The owner records a correction next to a closed
 * shift from their signed-in account; the server derives every figure from the originals plus the
 * active corrections and refuses what it would not accept. These sheets offer only that, and say
 * what a correction will do before it is recorded.
 */

/* ── what a correction is, in a line ── */
export const CORRECTION_STATUS_LABEL: Record<
  string,
  { label: string; tone: 'success' | 'neutral' | 'warning' | 'info' }
> = {
  active: { label: 'In effect', tone: 'success' },
  superseded: { label: 'Replaced', tone: 'neutral' },
  undone: { label: 'Undone', tone: 'warning' },
  undo: { label: 'Undo', tone: 'info' },
};

/** A pay-in's or a pay-out's category in words. */
export function categoryLabel(category: string): string {
  return (
    (PAY_IN_LABELS as Record<string, string>)[category] ??
    (PAY_OUT_LABELS as Record<string, string>)[category] ??
    category
  );
}

export function describeCorrection(
  c: ShiftCorrectionView,
  money: (minor: number) => string,
): string {
  const before = c.facts_before as Record<string, unknown>;
  const num = (o: Record<string, unknown>, k: string) =>
    typeof o[k] === 'number' ? (o[k] as number) : null;
  const str = (o: Record<string, unknown>, k: string) =>
    typeof o[k] === 'string' ? (o[k] as string) : null;
  switch (c.kind) {
    case 'count': {
      const from = num(before, 'counted_minor');
      return `Close count ${from != null ? `${money(from)} → ` : ''}${money(c.counted_minor ?? 0)} · ${
        c.cash_location ? CASH_LOCATION_LABELS[c.cash_location].toLowerCase() : ''
      }`;
    }
    case 'opening': {
      const from = num(before, 'opening_count_minor');
      return `Opening count ${from != null ? `${money(from)} → ` : ''}${money(c.counted_minor ?? 0)}`;
    }
    case 'method': {
      const from = str(before, 'method');
      return `${c.reversal_id ? 'Money given back' : 'Payment'}: ${from ?? '?'} → ${c.payment_method?.name ?? '?'}${
        c.reference_last4 ? ` (…${c.reference_last4})` : ''
      }`;
    }
    case 'pay_in':
    case 'pay_out': {
      const from = num(before, 'amount_minor');
      const cat = c.category ? categoryLabel(c.category) : '';
      return `${CORRECTION_KIND_LABELS[c.kind]} ${from != null && from !== c.amount_minor ? `${money(from)} → ` : ''}${money(
        c.amount_minor ?? 0,
      )}${cat ? ` · ${cat}` : ''}${c.receipt_ref ? ` · receipt ${c.receipt_ref}` : ''}`;
    }
    case 'settlement':
      return `${c.settlement_mode ? SETTLEMENT_MODE_LABELS[c.settlement_mode] : 'Settled'} ${money(c.amount_minor ?? 0)}${
        c.bearer ? ` · ${c.bearer.name}` : ''
      }${c.settled_to ? ` · ${SETTLED_TO_LABELS[c.settled_to].toLowerCase()}` : ''}${
        c.payment_id ? ' · on a non-cash row' : ''
      }`;
    case 'note':
      return 'Note';
    case 'payment_missed':
      return `Missed payment ${money(c.amount_minor ?? 0)} · ${c.payment_method?.name ?? '?'}${
        c.reference_last4 ? ` (…${c.reference_last4})` : ''
      }${c.order ? ` · ${c.order.order_number}` : ''}`;
    case 'refund_missed':
      return `Missed refund ${money(c.amount_minor ?? 0)} · ${c.payment_method?.name ?? '?'}${
        c.order ? ` · ${c.order.order_number}` : ''
      }`;
    case 'undo': {
      const undone = str(before, 'undone_kind') as CorrectionKind | null;
      const how =
        undone === 'payment_missed'
          ? ' (the payment voided)'
          : undone === 'refund_missed'
            ? ' (the refund cancelled)'
            : '';
      return `Undo of ${(CORRECTION_KIND_LABELS[undone as CorrectionKind] ?? 'a correction').toLowerCase()}${how}`;
    }
    default:
      return c.kind;
  }
}

/* ── the shift's corrections: what to do, and what was done ── */
export type ClassicTarget =
  | { kind: 'count' }
  | { kind: 'opening' }
  | { kind: 'note' }
  | { kind: 'settlement'; payment?: ShiftPaymentView }
  | { kind: 'method'; payment?: ShiftPaymentView; reversal?: ShiftReversalView }
  | { kind: 'pay_in' | 'pay_out'; movement: ShiftDetailMovement };
/** A correction (ADR-027) or money missed on the shift (ADR-028). */
export type CorrectionTarget = ClassicTarget | OwnerEntryTarget;
function isOwnerEntryTarget(t: CorrectionTarget): t is OwnerEntryTarget {
  return t.kind === 'payment_missed' || t.kind === 'refund_missed';
}

export function CorrectionsSection({
  detail,
  target,
  onTarget,
}: {
  detail: ShiftDetailView;
  target: CorrectionTarget | null;
  onTarget: (t: CorrectionTarget | null) => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const [undoing, setUndoing] = useState<ShiftCorrectionView | null>(null);
  const a = detail.adjusted;
  const closed = detail.shift.status === 'closed';
  const canSettle = closed && (a.over_short_minor ?? 0) < 0 && a.unsettled_minor > 0;
  return (
    <div className="d-stack" data-testid="corrections">
      {closed && (
        <div className="rv-action-grid">
          <Button
            size="sm"
            disabled={!online}
            onClick={() => onTarget({ kind: 'count' })}
            data-testid="correct-count"
          >
            Correct the close count
          </Button>
          {detail.shift.open_kind !== 'takeover' && (
            <Button
              size="sm"
              disabled={!online}
              onClick={() => onTarget({ kind: 'opening' })}
              data-testid="correct-opening"
            >
              Correct the opening count
            </Button>
          )}
          {canSettle && (
            <Button
              size="sm"
              disabled={!online}
              onClick={() => onTarget({ kind: 'settlement' })}
              data-testid="correct-settle"
            >
              Repay or write off
            </Button>
          )}
          <Button
            size="sm"
            disabled={!online}
            onClick={() => onTarget({ kind: 'note' })}
            data-testid="correct-note"
          >
            Add a note
          </Button>
          <Button
            size="sm"
            disabled={!online}
            onClick={() => onTarget({ kind: 'payment_missed' })}
            data-testid="entry-payment"
          >
            Record a missed payment
          </Button>
          <Button
            size="sm"
            disabled={!online}
            onClick={() => onTarget({ kind: 'refund_missed' })}
            data-testid="entry-refund"
          >
            Record a missed refund
          </Button>
        </div>
      )}
      <div className="d-list" data-testid="correction-list">
        {detail.corrections.map((c) => {
          const st = CORRECTION_STATUS_LABEL[c.status] ?? CORRECTION_STATUS_LABEL.active!;
          const past = c.status === 'superseded' || c.status === 'undone';
          return (
            <div
              key={c.id}
              className={`d-row rv-row--compact${past ? ' rv-row--past' : ''}`}
              data-testid="correction-row"
              data-kind={c.kind}
              data-status={c.status}
            >
              <div className="d-row__main">
                <div className="d-row__title">{describeCorrection(c, f.money)}</div>
                <div className="d-row__meta">
                  {f.at(c.at)} · {c.by.name} · “{c.reason}”
                  {typeof c.facts_before.over_short_minor === 'number' &&
                  typeof c.facts_after.over_short_minor === 'number' &&
                  c.facts_before.over_short_minor !== c.facts_after.over_short_minor
                    ? ` · ${describeOverShort(c.facts_before.over_short_minor, f.money)} → ${describeOverShort(
                        c.facts_after.over_short_minor,
                        f.money,
                      )}`
                    : ''}
                </div>
              </div>
              <div className="rv-side">
                <Badge tone={st.tone} dot>
                  {st.label}
                </Badge>
                {c.status === 'active' && (
                  <Button
                    size="sm"
                    disabled={!online}
                    onClick={() => setUndoing(c)}
                    data-testid="correction-undo"
                  >
                    Undo
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {detail.corrections.length === 0 && <EmptyState title="No corrections." />}
      </div>
      {target &&
        (isOwnerEntryTarget(target) ? (
          <OwnerEntrySheet detail={detail} target={target} onClose={() => onTarget(null)} />
        ) : (
          <CorrectionSheet detail={detail} target={target} onClose={() => onTarget(null)} />
        ))}
      {undoing && <UndoSheet correction={undoing} onClose={() => setUndoing(null)} />}
    </div>
  );
}

/* ── the shift's payments and money given back, to correct a method ── */
export function PaymentsSection({
  detail,
  onTarget,
}: {
  detail: ShiftDetailView;
  onTarget: (t: CorrectionTarget) => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const closed = detail.shift.status === 'closed';
  if (detail.payments.length === 0 && detail.reversals.length === 0)
    return <EmptyState title="No payments." />;
  return (
    <div className="d-list" data-testid="shift-payments">
      {detail.payments_more && (
        <Notice tone="info">
          The first 1,000 payments are listed; the rest are on the order list.
        </Notice>
      )}
      {detail.payments.map((p) => (
        <div
          key={p.id}
          className="d-row rv-row--compact"
          data-testid="payment-row"
          data-method={p.method.name}
          data-corrected={p.correction_id ? 'true' : 'false'}
        >
          <div className="d-row__main">
            <div className="rv-head">
              <div className="d-row__title">
                {p.order_number} · {p.method.name}
                {p.reference_last4 ? (
                  <span className="d-faint"> · …{p.reference_last4}</span>
                ) : null}
              </div>
              <span className="rv-money">{f.money(p.amount_minor)}</span>
            </div>
            <div className="d-row__meta">
              {f.at(p.recorded_at)}
              {p.received_by ? ` · ${p.received_by.name}` : ''}
              {p.late_entry ? ' · late entry' : ''}
              {p.owner_entry ? ' · recorded by the owner after the close' : ''}
              {p.recorded_method ? ` · recorded as ${p.recorded_method.name}` : ''}
            </div>
          </div>
          {p.voided ? (
            <div className="rv-side">
              <Badge tone={LEDGER_ROW_TONE.voided} dot>
                voided
              </Badge>
            </div>
          ) : closed ? (
            <div className="rv-side">
              <Button
                size="sm"
                disabled={!online}
                onClick={() => onTarget({ kind: 'method', payment: p })}
                data-testid="payment-method-correct"
              >
                Wrong method
              </Button>
              {p.reversible_minor > 0 && (
                <Button
                  size="sm"
                  disabled={!online}
                  onClick={() => onTarget({ kind: 'refund_missed', payment: p })}
                  data-testid="payment-refund-missed"
                >
                  Money given back, not keyed
                </Button>
              )}
            </div>
          ) : null}
        </div>
      ))}
      {detail.reversals.map((r) => (
        <div
          key={r.id}
          className="d-row rv-row--compact"
          data-testid="reversal-row"
          data-method={r.method.name}
          data-cancelled={r.cancelled ? 'true' : 'false'}
        >
          <div className="d-row__main">
            <div className="rv-head">
              <div className="d-row__title">
                {r.order_number} · {r.kind} · {r.method.name}
              </div>
              <span className="rv-money">{f.money(r.amount_minor)}</span>
            </div>
            <div className="d-row__meta">
              {f.at(r.recorded_at)}
              {r.owner_entry ? ' · recorded by the owner after the close' : ''}
              {r.cancel ? ` · cancelled ${f.at(r.cancel.at)}: “${r.cancel.reason}”` : ''}
              {r.recorded_method ? ` · recorded as ${r.recorded_method.name}` : ''}
              {r.method_override ? ' · by another method than the sale' : ''}
            </div>
          </div>
          {r.cancelled ? (
            <div className="rv-side">
              <Badge tone={LEDGER_ROW_TONE.cancelled} dot>
                cancelled
              </Badge>
            </div>
          ) : closed ? (
            <div className="rv-side">
              <Button
                size="sm"
                disabled={!online}
                onClick={() => onTarget({ kind: 'method', reversal: r })}
                data-testid="reversal-method-correct"
              >
                Wrong method
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/* ── the sheet ── */
const TITLE: Record<CorrectionKind, string> = {
  count: 'Correct the close count',
  opening: 'Correct the opening count',
  method: 'Wrong payment method',
  pay_in: 'Correct the pay-in',
  pay_out: 'Correct the pay-out',
  settlement: 'Repay or write off',
  note: 'Add a note',
  payment_missed: 'Record a missed payment',
  refund_missed: 'Record a missed refund',
};

function activeId(detail: ShiftDetailView, target: CorrectionTarget): string | null {
  const c = detail.corrections.find((x) => {
    if (x.status !== 'active') return false;
    switch (target.kind) {
      case 'count':
      case 'opening':
        return x.kind === target.kind;
      case 'method':
        return (
          x.kind === 'method' &&
          (target.payment
            ? x.payment_id === target.payment.id
            : x.reversal_id === (target.reversal?.id ?? ''))
        );
      case 'pay_in':
      case 'pay_out':
        return x.kind === target.kind && x.movement_id === target.movement.id;
      default:
        return false;
    }
  });
  return c?.id ?? null;
}

function CorrectionSheet({
  detail,
  target,
  onClose,
}: {
  detail: ShiftDetailView;
  target: ClassicTarget;
  onClose: () => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const { run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const a = detail.adjusted;
  const kind = target.kind;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState(() => {
    if (kind === 'count') return minorToInput(a.counted_minor ?? 0);
    if (kind === 'opening') return minorToInput(a.opening_count_minor);
    if (kind === 'pay_in' || kind === 'pay_out')
      return minorToInput(target.movement.effective?.amount_minor ?? target.movement.amount_minor);
    if (kind === 'settlement')
      return minorToInput(target.payment ? target.payment.amount_minor : a.unsettled_minor);
    return '';
  });
  const amountMinor = parseMoneyToMinor(amount);
  const [where, setWhere] = useState<CashLocation | ''>('');
  const [linked, setLinked] = useState('');
  const [methodId, setMethodId] = useState('');
  const [reference, setReference] = useState('');
  const [category, setCategory] = useState<string>(() =>
    kind === 'pay_in' || kind === 'pay_out'
      ? (target.movement.effective?.category ?? target.movement.category ?? '')
      : '',
  );
  const [receipt, setReceipt] = useState(() =>
    kind === 'pay_out'
      ? (target.movement.effective?.receipt_ref ?? target.movement.receipt_ref ?? '')
      : '',
  );
  const [mode, setMode] = useState<SettlementMode>('repaid');
  const [bearer, setBearer] = useState('');
  const [settledTo, setSettledTo] = useState<SettledTo>('owner');
  const methods = useQuery({
    queryKey: paymentKeys.methods,
    queryFn: fetchPaymentMethods,
    enabled: kind === 'method',
  });
  const employees = useQuery({
    queryKey: ['employees', 'active-names'],
    enabled: kind === 'settlement',
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employee')
        .select('id, display_name')
        .eq('status', 'active')
        .order('display_name');
      if (error) throw error;
      return data ?? [];
    },
  });
  const current =
    kind === 'method' ? (target.payment?.method ?? target.reversal?.method ?? null) : null;
  const chosenMethod = methods.data?.find((m) => m.id === methodId) ?? null;
  // the preview moves from the figures as corrected; where the cash is follows the server's rule on the figures
  // as recorded at the close
  const delta = kind === 'count' && amountMinor != null ? amountMinor - (a.counted_minor ?? 0) : 0;
  const sh = detail.shift;
  const deltaOriginal =
    kind === 'count' && amountMinor != null ? amountMinor - (sh.counted_minor ?? 0) : 0;
  const locations = useMemo(
    () =>
      kind === 'count'
        ? cashLocationsFor(deltaOriginal, sh.removed_minor ?? 0, sh.retained_minor ?? 0)
        : [],
    [kind, deltaOriginal, sh.removed_minor, sh.retained_minor],
  );
  const linkable: LinkablePayIn[] = detail.linkable_pay_ins;
  const needsLink =
    (kind === 'count' && where === 'open_drawer') ||
    (kind === 'settlement' && mode === 'repaid' && settledTo === 'drawer');
  const linkCandidates = linkable.filter((m) =>
    kind === 'count' ? m.amount_minor === deltaOriginal : m.amount_minor === amountMinor,
  );
  const preview = (() => {
    if (a.over_short_minor == null) return null;
    if (kind === 'count' && amountMinor != null) return a.over_short_minor + delta;
    if (kind === 'opening' && amountMinor != null)
      return a.over_short_minor - (amountMinor - a.opening_count_minor);
    if ((kind === 'pay_in' || kind === 'pay_out') && amountMinor != null) {
      const now = target.movement.effective?.amount_minor ?? target.movement.amount_minor;
      return kind === 'pay_in'
        ? a.over_short_minor - (amountMinor - now)
        : a.over_short_minor + (amountMinor - now);
    }
    if (kind === 'method' && chosenMethod && current) {
      const amt = target.payment?.amount_minor ?? target.reversal?.amount_minor ?? 0;
      const sign = target.reversal ? -1 : 1;
      if (current.kind === 'cash' && chosenMethod.kind !== 'cash')
        return a.over_short_minor + sign * amt;
      if (current.kind !== 'cash' && chosenMethod.kind === 'cash')
        return a.over_short_minor - sign * amt;
      return a.over_short_minor;
    }
    return null;
  })();
  const valid =
    reason.trim().length > 0 &&
    (kind === 'note' ||
      (kind === 'count' &&
        amountMinor != null &&
        deltaOriginal !== 0 &&
        !!where &&
        (!needsLink || !!linked)) ||
      (kind === 'opening' && amountMinor != null && amountMinor !== a.opening_count_minor) ||
      (kind === 'method' &&
        !!chosenMethod &&
        chosenMethod.id !== current?.id &&
        (!chosenMethod.requires_reference || reference.trim().length > 0)) ||
      ((kind === 'pay_in' || kind === 'pay_out') &&
        amountMinor != null &&
        amountMinor > 0 &&
        !!category &&
        (kind !== 'pay_out' || receipt.trim().length > 0)) ||
      (kind === 'settlement' &&
        amountMinor != null &&
        amountMinor > 0 &&
        (mode === 'written_off' || (!!bearer && (!needsLink || !!linked)))));

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await record();
    } finally {
      setBusy(false);
    }
  };
  const record = async () => {
    if (correctionNeedsRecentAuth(kind) && !(await ensureFresh())) return;
    const base = { shift_id: detail.shift.id, reason: reason.trim() };
    const replaces = { replaces_id: activeId(detail, target) };
    let payload: ShiftCorrectPayload;
    switch (kind) {
      case 'count':
        payload = {
          ...base,
          ...replaces,
          kind,
          counted_minor: amountMinor!,
          cash_location: where as CashLocation,
          ...(where === 'open_drawer' ? { linked_movement_id: linked } : {}),
        };
        break;
      case 'opening':
        payload = { ...base, ...replaces, kind, counted_minor: amountMinor! };
        break;
      case 'method':
        payload = {
          ...base,
          ...replaces,
          kind,
          ...(target.payment
            ? { payment_id: target.payment.id }
            : { reversal_id: target.reversal!.id }),
          payment_method_id: methodId,
          ...(chosenMethod?.requires_reference ? { reference: reference.trim() } : {}),
        };
        break;
      case 'pay_in':
        payload = {
          ...base,
          ...replaces,
          kind,
          movement_id: target.movement.id,
          amount_minor: amountMinor!,
          category: category as PayInCategory,
        };
        break;
      case 'pay_out':
        payload = {
          ...base,
          ...replaces,
          kind,
          movement_id: target.movement.id,
          amount_minor: amountMinor!,
          category: category as PayOutCategory,
          receipt_ref: receipt.trim(),
        };
        break;
      case 'settlement':
        payload = {
          ...base,
          kind,
          amount_minor: amountMinor!,
          settlement_mode: mode,
          ...(bearer ? { bearer_employee_id: bearer } : {}),
          ...(mode === 'repaid' ? { settled_to: settledTo } : {}),
          ...(needsLink ? { linked_movement_id: linked } : {}),
          ...(target.payment ? { payment_id: target.payment.id } : {}),
        };
        break;
      default:
        payload = { ...base, kind: 'note' };
    }
    const r = await run('shift.correct', payload);
    if (r) {
      toast(kind === 'note' ? 'Note added' : 'Corrected');
      onClose();
    }
  };

  return (
    <Sheet open onClose={onClose} title={TITLE[kind]}>
      <div className="d-stack" data-testid="correction-sheet" data-kind={kind}>
        {kind === 'count' && (
          <p className="d-muted">
            Counted at the close: {f.money(detail.shift.counted_minor ?? 0)}
            {a.count_correction_id ? ` · reads ${f.money(a.counted_minor ?? 0)} as corrected` : ''}.
            The close reads {describeOverShort(a.over_short_minor ?? 0, f.money).toLowerCase()}.
          </p>
        )}
        {kind === 'opening' && (
          <p className="d-muted" data-testid="opening-as-recorded">
            {openingCountText(
              (detail.frozen ?? detail.live).cash.opening_count_minor,
              a.opening_count_minor,
              f.money,
            )}
          </p>
        )}
        {kind === 'method' && methods.error ? (
          <ErrorState
            title="Could not load the payment methods"
            message={errorText(methods.error)}
            onRetry={() => void methods.refetch()}
          />
        ) : null}
        {kind === 'settlement' && employees.error ? (
          <ErrorState
            title="Could not load the employees"
            message={errorText(employees.error)}
            onRetry={() => void employees.refetch()}
          />
        ) : null}
        {kind === 'method' && current && (
          <p className="d-muted">
            {target.payment?.order_number ?? target.reversal?.order_number} ·{' '}
            {f.money(target.payment?.amount_minor ?? target.reversal?.amount_minor ?? 0)} · recorded
            as {current.name}
          </p>
        )}
        {(kind === 'pay_in' || kind === 'pay_out') && (
          <p className="d-muted">
            As recorded: {f.money(target.movement.amount_minor)}
            {target.movement.category ? ` · ${categoryLabel(target.movement.category)}` : ''}
            {target.movement.payee ? ` · ${target.movement.payee}` : ''}
            {target.movement.reason ? ` · “${target.movement.reason}”` : ''}
          </p>
        )}
        {kind === 'settlement' && (
          <p className="d-muted">
            {target.payment
              ? `${target.payment.order_number} · ${f.money(target.payment.amount_minor)} · ${target.payment.method.name}`
              : `${describeOverShort(a.over_short_minor ?? 0, f.money)} · ${f.money(a.unsettled_minor)} still unsettled`}
          </p>
        )}

        {(kind === 'count' ||
          kind === 'opening' ||
          kind === 'pay_in' ||
          kind === 'pay_out' ||
          kind === 'settlement') && (
          <Field
            label={
              kind === 'count'
                ? 'The real close count'
                : kind === 'opening'
                  ? 'The real opening count'
                  : kind === 'settlement'
                    ? mode === 'repaid'
                      ? 'Amount repaid'
                      : 'Amount written off'
                    : 'The real amount'
            }
            help="In pesos"
          >
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                className="d-input--money"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="correction-amount"
                autoFocus
              />
            )}
          </Field>
        )}
        {kind === 'count' && deltaOriginal !== 0 && (
          <Field
            label="Where that cash is now"
            help={
              locations.length === 0
                ? 'Below both what was kept and what was removed: no single place explains it'
                : 'A correction says where the money is'
            }
          >
            {() => (
              <Pills
                ariaLabel="Where the cash is"
                value={where}
                onChange={setWhere}
                options={locations.map((l) => ({ value: l, label: CASH_LOCATION_LABELS[l] }))}
              />
            )}
          </Field>
        )}
        {kind === 'method' && (
          <Field label="The method it was really paid by">
            {(id) => (
              <Select
                id={id}
                value={methodId}
                onChange={(e) => setMethodId(e.target.value)}
                data-testid="correction-method"
              >
                <option value="">Choose a method</option>
                {(methods.data ?? [])
                  .filter((m) => m.enabled && !m.archived_at && m.kind !== 'complimentary')
                  .map((m) => (
                    <option key={m.id} value={m.id} disabled={m.id === current?.id}>
                      {m.name}
                      {m.id === current?.id ? ' (as recorded)' : ''}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        )}
        {kind === 'method' && chosenMethod?.requires_reference && (
          <Field label={`${chosenMethod.name} reference`} help="From the provider's record">
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={64}
                onChange={(e) => setReference(e.target.value)}
                data-testid="correction-reference"
              />
            )}
          </Field>
        )}
        {(kind === 'pay_in' || kind === 'pay_out') && (
          <Field label="Category">
            {() => (
              <Pills
                ariaLabel="Category"
                value={category}
                onChange={setCategory}
                options={
                  kind === 'pay_in'
                    ? PAY_IN_CATEGORIES.map((c) => ({
                        value: c as string,
                        label: PAY_IN_LABELS[c],
                      }))
                    : PAY_OUT_CATEGORIES.map((c) => ({
                        value: c as string,
                        label: PAY_OUT_LABELS[c],
                      }))
                }
              />
            )}
          </Field>
        )}
        {kind === 'pay_out' && (
          <Field label="Receipt reference" help="Required: a corrected pay-out carries its receipt">
            {(id) => (
              <Input
                id={id}
                value={receipt}
                maxLength={64}
                onChange={(e) => setReceipt(e.target.value)}
                data-testid="correction-receipt"
              />
            )}
          </Field>
        )}
        {kind === 'settlement' && (
          <>
            <Field label="How">
              {() => (
                <Pills
                  ariaLabel="How"
                  value={mode}
                  onChange={setMode}
                  options={SETTLEMENT_MODES.map((m) => ({
                    value: m,
                    label: SETTLEMENT_MODE_LABELS[m],
                  }))}
                />
              )}
            </Field>
            <Field
              label={mode === 'repaid' ? 'Who repaid it' : 'Who is responsible'}
              help={mode === 'repaid' ? 'Required' : 'Optional: the café bears it'}
            >
              {(id) => (
                <Select
                  id={id}
                  value={bearer}
                  onChange={(e) => setBearer(e.target.value)}
                  data-testid="correction-bearer"
                >
                  <option value="">{mode === 'repaid' ? 'Choose' : 'Nobody named'}</option>
                  {(employees.data ?? []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.display_name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {mode === 'repaid' && (
              <Field label="Where the money went">
                {() => (
                  <Pills
                    ariaLabel="Where the money went"
                    value={settledTo}
                    onChange={setSettledTo}
                    options={SETTLED_TO.map((t) => ({ value: t, label: SETTLED_TO_LABELS[t] }))}
                  />
                )}
              </Field>
            )}
          </>
        )}
        {needsLink && (
          <Field
            label="The pay-in it went into"
            help={
              linkCandidates.length === 0
                ? 'No unlinked pay-in of that amount on a later shift: record it on the tablet first'
                : 'A pay-in recorded on the tablet after this close, for exactly this amount'
            }
          >
            {(id) => (
              <Select
                id={id}
                value={linked}
                onChange={(e) => setLinked(e.target.value)}
                data-testid="correction-link"
              >
                <option value="">Choose a pay-in</option>
                {linkCandidates.map((m) => (
                  <option key={m.id} value={m.id}>
                    #{m.shift_seq} · {f.money(m.amount_minor)} · {f.at(m.recorded_at)} · {m.by.name}
                    {m.reason ? ` · “${m.reason}”` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={kind === 'note' ? 'Note' : 'Why'} help="Required">
          {(id) => (
            <Input
              id={id}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              data-testid="correction-reason"
              autoFocus={kind === 'note'}
            />
          )}
        </Field>
        {preview != null && kind !== 'settlement' && (
          <Notice tone="info" testId="correction-preview">
            The close will read {describeOverShort(preview, f.money).toLowerCase()}.
          </Notice>
        )}
        <Button
          variant="primary"
          size="lg"
          block
          busy={busy}
          disabled={!online || !valid}
          onClick={() => void submit()}
          data-testid="correction-confirm"
        >
          {kind === 'note' ? 'Add the note' : 'Record the correction'}
        </Button>
      </div>
    </Sheet>
  );
}

function UndoSheet({
  correction,
  onClose,
}: {
  correction: ShiftCorrectionView;
  onClose: () => void;
}) {
  const f = useFormats();
  const online = useOnline();
  const { run, ensureFresh } = useOwnerReview();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (!(await ensureFresh())) return;
      const r = await run('shift.correct_undo', {
        correction_id: correction.id,
        reason: reason.trim(),
      });
      if (r) {
        toast('Undone');
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title="Undo this correction">
      <div className="d-stack" data-testid="undo-sheet">
        <p className="d-strong d-break">{describeCorrection(correction, f.money)}</p>
        <p className="d-help">
          The figures go back to what they read before it. A correction it replaced is not revived;
          record a new one instead.
        </p>
        <Field label="Why" help="Required">
          {(id) => (
            <Input
              id={id}
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              data-testid="undo-reason"
              autoFocus
            />
          )}
        </Field>
        <Button
          variant="danger-solid"
          size="lg"
          block
          busy={busy}
          disabled={!online || reason.trim().length === 0}
          onClick={() => void submit()}
          data-testid="undo-confirm"
        >
          Undo
        </Button>
      </div>
    </Sheet>
  );
}
